import { c as _c } from "react/compiler-runtime";
import { useFormContext, useWatch } from "react-hook-form";
import { AddNewRoomListingSchemaType, ListingType } from "../../../api/schema";
import { useTranslations } from "next-intl";
import useSession from "@/hooks/QueryHooks/useSession";
import { useNewRoomListingContext } from "../../NewRoomListingContext";
import { useAdminUser } from "@/components/modules/admin/api/queries/useAdminUsers";
import { useLocationsById } from "@/hooks/QueryHooks/useLocationsById";
import { useBoundaries } from "@/hooks/useBoundaries";
import { administrativeLevelForCode } from "@/lib/boundaries/boundaryCache";
import { convertPolygon } from "@/components/pages/search/map/components/MapPolygons";
import { toFeaturePillItems } from "@/constants/features";
import { ROOM_FLAG_KEYS } from "../house-details/useHouseDetails";
import { PawPrint, FileText, Cigarette, type LucideIcon } from "lucide-react";
const RULE_ICONS: Record<(typeof ROOM_FLAG_KEYS)[number], LucideIcon> = {
  petFriendly: PawPrint,
  contract: FileText,
  smokingAllowed: Cigarette
};
export function useRevisionDetails() {
  const $ = _c(88);
  const {
    getValues,
    control
  } = useFormContext();
  const t = useTranslations("modules.listings.addRoomListing.review");
  const tFeatures = useTranslations("modules.listings.addRoomListing.features");
  const tHouse = useTranslations("modules.listings.addRoomListing.houseDetails");
  const session = useSession();
  const {
    adminTargetUserId
  } = useNewRoomListingContext();
  const targetUser = useAdminUser(adminTargetUserId ?? "");
  let t0;
  if ($[0] !== adminTargetUserId || $[1] !== session || $[2] !== targetUser) {
    t0 = adminTargetUserId ? {
      image: targetUser.data?.data?.image,
      name: targetUser.data?.data?.name,
      bio: targetUser.data?.data?.bio
    } : {
      image: session.data?.image,
      name: session.data?.name,
      bio: session.data?.bio
    };
    $[0] = adminTargetUserId;
    $[1] = session;
    $[2] = targetUser;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const profile = t0;
  let t1;
  if ($[4] !== getValues) {
    t1 = getValues("listingType");
    $[4] = getValues;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const isDemand = t1 === ListingType.DEMAND;
  let t2;
  if ($[6] !== getValues) {
    t2 = getValues("title");
    $[6] = getValues;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const title = t2;
  let t3;
  if ($[8] !== getValues) {
    t3 = getValues("description");
    $[8] = getValues;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const description = t3;
  let t4;
  if ($[10] !== getValues) {
    t4 = getValues("latitude");
    $[10] = getValues;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const latitude = t4;
  let t5;
  if ($[12] !== getValues) {
    t5 = getValues("longitude");
    $[12] = getValues;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const longitude = t5;
  let t6;
  if ($[14] !== getValues) {
    t6 = getValues("rentPrice") ? getValues("rentPrice") : "-";
    $[14] = getValues;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const price = t6;
  let t7;
  if ($[16] !== getValues) {
    t7 = getValues("deposit") ? getValues("deposit") : "-";
    $[16] = getValues;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const deposit = t7;
  let t8;
  if ($[18] !== getValues) {
    t8 = getValues("bedrooms") ? String(getValues("bedrooms")) : "-";
    $[18] = getValues;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const bedrooms = t8;
  let t9;
  if ($[20] !== getValues) {
    t9 = getValues("bathrooms") ? String(getValues("bathrooms")) : "-";
    $[20] = getValues;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const bathrooms = t9;
  let t10;
  if ($[22] !== getValues) {
    t10 = getValues("maxPersons") ? String(getValues("maxPersons")) : "-";
    $[22] = getValues;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const maxPersons = t10;
  let t11;
  if ($[24] !== getValues) {
    t11 = getValues("gender");
    $[24] = getValues;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const gender = t11;
  let t12;
  if ($[26] !== getValues) {
    t12 = getValues("locationNormalizedName");
    $[26] = getValues;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const localization = t12;
  let t13;
  if ($[28] !== getValues) {
    t13 = getValues("availableFrom") || new Date();
    $[28] = getValues;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const availableFrom = t13;
  let t14;
  if ($[30] !== getValues) {
    t14 = getValues("images");
    $[30] = getValues;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const images = t14;
  const cap = _temp;
  let t15;
  if ($[32] !== getValues || $[33] !== tFeatures) {
    t15 = toFeaturePillItems(getValues("roomFeatures") ?? [], tFeatures);
    $[32] = getValues;
    $[33] = tFeatures;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const roomFeatures = t15;
  let t16;
  if ($[35] !== getValues || $[36] !== tFeatures) {
    t16 = toFeaturePillItems(getValues("houseFeatures") ?? [], tFeatures);
    $[35] = getValues;
    $[36] = tFeatures;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const houseFeatures = t16;
  let t17;
  if ($[38] !== getValues || $[39] !== isDemand || $[40] !== tHouse) {
    let t18;
    if ($[42] !== getValues) {
      t18 = key => getValues(key);
      $[42] = getValues;
      $[43] = t18;
    } else {
      t18 = $[43];
    }
    let t19;
    if ($[44] !== isDemand || $[45] !== tHouse) {
      t19 = key_0 => ({
        key: key_0,
        label: isDemand ? tHouse(`demand${cap(key_0)}`) : tHouse(key_0),
        Icon: RULE_ICONS[key_0]
      });
      $[44] = isDemand;
      $[45] = tHouse;
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    t17 = ROOM_FLAG_KEYS.filter(t18).map(t19);
    $[38] = getValues;
    $[39] = isDemand;
    $[40] = tHouse;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const houseRules = t17;
  const locationCodes = getValues("locationCodes") ?? [];
  let t18;
  if ($[47] !== control) {
    t18 = {
      control,
      name: "associatedUnitId"
    };
    $[47] = control;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const associatedUnitId = useWatch(t18);
  let t19;
  if ($[49] !== control) {
    t19 = {
      control,
      name: "associatedUnitTitle"
    };
    $[49] = control;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  const associatedUnitTitle = useWatch(t19);
  let t20;
  if ($[51] !== control) {
    t20 = {
      control,
      name: "associatedPortfolioTitle"
    };
    $[51] = control;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const associatedPortfolioTitle = useWatch(t20);
  const associatedPropertyImages = useWatch({
    control,
    name: "associatedPropertyImages"
  }) ?? [];
  const {
    data: locationPolygonData
  } = useLocationsById({
    locations: locationCodes,
    enabled: isDemand && locationCodes.length > 0
  });
  let t21;
  if ($[53] !== locationPolygonData?.data) {
    t21 = locationPolygonData?.data ?? [];
    $[53] = locationPolygonData?.data;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t21) {
    t22 = t21.map(_temp2);
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  const selectedLocations = t22;
  const geometryByCode = useBoundaries(locationCodes.map(_temp3));
  let t23;
  if ($[57] !== geometryByCode) {
    t23 = code_0 => convertPolygon(geometryByCode.get(code_0)) as [number, number][];
    $[57] = geometryByCode;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const locationPolygons = locationCodes.map(t23).filter(_temp4);
  let t24;
  if ($[59] !== associatedPortfolioTitle || $[60] !== associatedPropertyImages || $[61] !== associatedUnitId || $[62] !== associatedUnitTitle || $[63] !== availableFrom || $[64] !== bathrooms || $[65] !== bedrooms || $[66] !== control || $[67] !== deposit || $[68] !== description || $[69] !== gender || $[70] !== houseFeatures || $[71] !== houseRules || $[72] !== images || $[73] !== isDemand || $[74] !== latitude || $[75] !== localization || $[76] !== locationCodes || $[77] !== locationPolygons || $[78] !== longitude || $[79] !== maxPersons || $[80] !== price || $[81] !== profile || $[82] !== roomFeatures || $[83] !== selectedLocations || $[84] !== session || $[85] !== t || $[86] !== title) {
    t24 = {
      control,
      t,
      session,
      profile,
      isDemand,
      title,
      description,
      latitude,
      longitude,
      price,
      deposit,
      bedrooms,
      bathrooms,
      maxPersons,
      gender,
      roomFeatures,
      houseFeatures,
      houseRules,
      localization,
      availableFrom,
      images,
      locationCodes,
      selectedLocations,
      locationPolygons,
      associatedUnitId,
      associatedUnitTitle,
      associatedPortfolioTitle,
      associatedPropertyImages
    };
    $[59] = associatedPortfolioTitle;
    $[60] = associatedPropertyImages;
    $[61] = associatedUnitId;
    $[62] = associatedUnitTitle;
    $[63] = availableFrom;
    $[64] = bathrooms;
    $[65] = bedrooms;
    $[66] = control;
    $[67] = deposit;
    $[68] = description;
    $[69] = gender;
    $[70] = houseFeatures;
    $[71] = houseRules;
    $[72] = images;
    $[73] = isDemand;
    $[74] = latitude;
    $[75] = localization;
    $[76] = locationCodes;
    $[77] = locationPolygons;
    $[78] = longitude;
    $[79] = maxPersons;
    $[80] = price;
    $[81] = profile;
    $[82] = roomFeatures;
    $[83] = selectedLocations;
    $[84] = session;
    $[85] = t;
    $[86] = title;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  return t24;
}
function _temp4(p) {
  return p.length > 0;
}
function _temp3(code) {
  return {
    locationCode: code,
    administrative_level: administrativeLevelForCode(code)
  };
}
function _temp2(loc) {
  return {
    code: loc.code ?? "",
    name: loc.name
  };
}
function _temp(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}